import axios from 'axios';
import { useSyncExternalStore } from 'react';
import { createStore, get, set, del, keys, delMany, values } from 'idb-keyval';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MUTATIONS = ['post', 'put', 'patch', 'delete'];
const WARMUP_PATHS = [
    '/auth/me', '/event/status', '/dashboard/stats', '/dashboard/etat-general', '/care-centers', '/vehicles',
    '/vehicles/my-vehicle', '/persons', '/incidents', '/crisis-directory', '/resources', '/missions',
    '/ordres-mission', '/procedures', '/procedures/categories', '/organigramme', '/personnel/status',
    '/shifts/engaged', '/map/drawings', '/map/buildings', '/services-techniques/zones-dangereuses', '/main-courante',
    '/settings/commune', '/notifications', '/dashboard/commune',
];
const RESOURCE_LABELS = {
    persons: 'Personne', vehicles: 'Véhicule', 'care-centers': 'Centre CARE', incidents: 'Incident',
    resources: 'Moyen', missions: 'Mission', 'main-courante': 'Main courante', 'ordres-mission': 'Ordre de mission',
    'material-requests': 'Demande de matériel', personnel: 'Personnel', shifts: 'Roulement',
    'services-techniques': 'Service communal', 'journal-bord': 'Journal de bord', 'crisis-directory': 'Annuaire',
    documents: 'Document', notifications: 'Notification', map: 'Carte',
};
const VERBS = { post: 'Création', put: 'Modification', patch: 'Modification', delete: 'Suppression' };

const cacheStore = createStore('civis-offline-cache', 'responses');
const queueStore = createStore('civis-offline-queue', 'actions');

let state = { online: navigator.onLine, pending: 0, syncing: false, issues: [], lastDataAt: localStorage.getItem('civis-last-data-at') };
const listeners = new Set();
const update = (patch) => { state = { ...state, ...patch }; listeners.forEach((l) => l()); };
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
export const useOfflineStatus = () => useSyncExternalStore(subscribe, () => state);

export const getScope = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
        const p = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return `${p.tenant || 'default'}:${p.sub}`;
    } catch {
        return null;
    }
};

const isApi = (config) => (config.url || '').startsWith(API) || (config.url || '').startsWith('/api');
const apiPath = (config) => (config.url || '').replace(API, '').replace(/^\/api/, '').split('?')[0];
const cacheKey = (config) => `${getScope()}|${axios.getUri(config)}`;
const NOT_QUEUED = ['/auth', '/vehicles/my-vehicle'];
const canQueue = (config) => MUTATIONS.includes(config.method) && !NOT_QUEUED.some((p) => apiPath(config).startsWith(p))
    && !apiPath(config).endsWith('/update-location')
    && !(config.data instanceof FormData) && config.responseType !== 'blob';

const describe = (method, path) => {
    const resource = path.split('/')[1];
    return `${VERBS[method]} · ${RESOURCE_LABELS[resource] || resource}`;
};

const offlineError = (message) => Object.assign(new Error(message), {
    isOffline: true, response: { status: 503, data: { detail: message } },
});

const parseBody = (data) => {
    if (typeof data !== 'string') return data || {};
    try { return JSON.parse(data); } catch { return {}; }
};

async function enqueue(config) {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const path = apiPath(config);
    await set(id, {
        id, scope: getScope(), method: config.method, url: config.url, data: config.data,
        queued_at: new Date().toISOString(), label: describe(config.method, path), path, status: 'pending',
    }, queueStore);
    await refreshQueue();
    toast.info('Hors ligne : saisie conservée sur cet appareil, envoi automatique au retour du réseau.');
    const body = parseBody(config.data);
    return {
        data: { ...(Array.isArray(body) ? {} : body), id: `hors-ligne-${id}`, _offline: true, message: 'Enregistré hors ligne' },
        status: 202, statusText: 'Accepted (offline)', headers: {}, config, offlineQueued: true,
    };
}

async function offlineAdapter(config) {
    if (config.method === 'get' && config.responseType !== 'blob') {
        const cached = await get(cacheKey(config), cacheStore);
        if (cached) return { data: cached.data, status: 200, statusText: 'OK (cache)', headers: {}, config, fromCache: true };
        throw offlineError('Donnée non disponible hors ligne (jamais consultée sur cet appareil).');
    }
    if (canQueue(config)) return enqueue(config);
    throw offlineError('Action indisponible hors ligne (envoi de fichier, export ou connexion).');
}

async function scopedItems() {
    const scope = getScope();
    const all = await values(queueStore);
    return all.filter((i) => i.scope === scope).sort((a, b) => a.id.localeCompare(b.id));
}

export async function refreshQueue() {
    const items = await scopedItems();
    update({ pending: items.filter((i) => i.status === 'pending').length, issues: items.filter((i) => i.status !== 'pending') });
}

const setOnline = (online) => { if (state.online !== online) update({ online }); };

export async function syncQueue() {
    if (state.syncing || !navigator.onLine || !getScope()) return;
    const pending = (await scopedItems()).filter((i) => i.status === 'pending');
    if (!pending.length) return refreshQueue();
    update({ syncing: true });
    const result = { ok: 0, conflict: 0, failed: 0 };
    for (const item of pending) {
        try {
            await axios({
                method: item.method, url: item.url, data: item.data, _offlineReplay: true,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, 'X-Offline-Queued-At': item.queued_at },
            });
            await del(item.id, queueStore);
            result.ok += 1;
        } catch (e) {
            if (!e.response) { setOnline(false); break; }
            if (e.response.status === 401) { toast.error('Session expirée : reconnectez-vous pour envoyer les saisies en attente.'); break; }
            const kind = e.response.status === 409 && e.response.data?.conflict ? 'conflict' : 'failed';
            await set(item.id, { ...item, status: kind, error: e.response.data?.detail || `Erreur ${e.response.status}` }, queueStore);
            result[kind] += 1;
        }
    }
    update({ syncing: false });
    await refreshQueue();
    if (result.ok) toast.success(`${result.ok} saisie(s) hors ligne synchronisée(s).`);
    if (result.conflict) toast.warning(`${result.conflict} conflit(s) : la modification la plus récente a été conservée.`);
    if (result.failed) toast.error(`${result.failed} saisie(s) refusée(s) par le serveur.`);
    window.dispatchEvent(new Event('civis-offline-synced'));
}

export async function retryItem(id) {
    const item = await get(id, queueStore);
    if (item) await set(id, { ...item, status: 'pending', error: null }, queueStore);
    await refreshQueue();
    syncQueue();
}

export async function dismissItem(id) {
    await del(id, queueStore);
    await refreshQueue();
}

export async function clearOfflineCache() {
    const scope = getScope();
    if (!scope) return;
    const all = await keys(cacheStore);
    await delMany(all.filter((k) => String(k).startsWith(`${scope}|`)), cacheStore);
}

export async function warmupCache() {
    const token = localStorage.getItem('token');
    const last = Number(sessionStorage.getItem('civis-warmup-at') || 0);
    if (!token || !navigator.onLine || Date.now() - last < 10 * 60 * 1000) return;
    sessionStorage.setItem('civis-warmup-at', String(Date.now()));
    const headers = { Authorization: `Bearer ${token}` };
    await Promise.allSettled(WARMUP_PATHS.map((p) => axios.get(`${API}${p}`, { headers })));
}

function installInterceptors() {
    axios.interceptors.request.use((config) => {
        if (!config._offlineReplay && isApi(config) && !navigator.onLine) config.adapter = offlineAdapter;
        return config;
    });
    axios.interceptors.response.use((response) => {
        const { config } = response;
        if (config._offlineReplay || !isApi(config) || response.fromCache || response.offlineQueued) return response;
        setOnline(true);
        if (config.method === 'get' && config.responseType !== 'blob' && getScope()) {
            const at = new Date().toISOString();
            set(cacheKey(config), { data: response.data, at }, cacheStore);
            localStorage.setItem('civis-last-data-at', at);
            update({ lastDataAt: at });
        }
        return response;
    }, (error) => {
        const config = error.config;
        if (error.response || !config || config._offlineReplay || !isApi(config) || axios.isCancel(error)) return Promise.reject(error);
        setOnline(false);
        return offlineAdapter(config);
    });
}

function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) return;
    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch((e) => console.error('Service worker:', e));
    });
}

export function initOffline() {
    installInterceptors();
    registerServiceWorker();
    window.addEventListener('offline', () => setOnline(false));
    window.addEventListener('online', () => { setOnline(true); setTimeout(syncQueue, 1000); });
    setInterval(() => { if (state.pending && navigator.onLine) syncQueue(); }, 30000);
    refreshQueue().then(syncQueue);
}
